import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Button from '../components/ui/Button';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
        <p className="text-muted-foreground mb-8">Last updated: December 12, 2025</p>

        <div className="prose prose-slate max-w-none space-y-6">
          <section>
            <h2 className="text-2xl font-semibold mb-3">1. Introduction</h2>
            <p className="text-muted-foreground">
              Earn My Edge ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains 
              how we collect, use, disclose, and safeguard your information when you use our sales training platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">2. Information We Collect</h2>
            
            <div className="space-y-3">
              <p className="text-muted-foreground">
                <strong>2.1 Information You Provide:</strong>
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Account information (name, email address, password)</li>
                <li>Profile information (industry, role, experience level)</li>
                <li>Practice drill responses and recordings</li>
                <li>Payment information (processed by our payment provider, Whop)</li>
                <li>Communications with us (support requests, feedback)</li>
              </ul>

              <p className="text-muted-foreground mt-4">
                <strong>2.2 Automatically Collected Information:</strong>
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Device information (browser type, operating system, IP address)</li>
                <li>Usage data (pages visited, features used, time spent)</li>
                <li>Session information (device fingerprint, geolocation)</li>
                <li>Performance metrics (drill scores, completion rates)</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>

              <p className="text-muted-foreground mt-4">
                <strong>2.3 AI Processing Data:</strong>
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Your practice responses for AI evaluation</li>
                <li>Audio recordings (if you use voice features)</li>
                <li>Performance patterns for personalized recommendations</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">3. How We Use Your Information</h2>
            <div className="space-y-3">
              <p className="text-muted-foreground">We use your information to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Provide, maintain, and improve the Service</li>
                <li>Process your transactions and manage subscriptions</li>
                <li>Generate AI-powered coaching feedback and recommendations</li>
                <li>Track your progress and provide performance analytics</li>
                <li>Personalize your learning experience</li>
                <li>Send you notifications about your account and new features</li>
                <li>Respond to your requests and provide customer support</li>
                <li>Detect and prevent fraud, abuse, or security issues</li>
                <li>Comply with legal obligations</li>
                <li>Conduct research and analytics to improve our Service</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">4. Session Management and Security</h2>
            <p className="text-muted-foreground">
              We enforce a maximum of 2 concurrent active sessions per account to prevent unauthorized account sharing. 
              We collect device fingerprints and IP geolocation to identify and manage sessions. This information is used 
              solely for security purposes and to enforce our Terms of Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">5. Information Sharing and Disclosure</h2>
            <div className="space-y-3">
              <p className="text-muted-foreground">
                <strong>5.1 Third-Party Service Providers:</strong>
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Supabase:</strong> Database and authentication services</li>
                <li><strong>Whop:</strong> Payment processing and subscription management</li>
                <li><strong>Puter AI / OpenAI:</strong> AI-powered coaching features</li>
                <li><strong>Vercel:</strong> Hosting and deployment</li>
              </ul>

              <p className="text-muted-foreground mt-4">
                <strong>5.2 We Do Not Sell Your Data:</strong> We do not sell, rent, or trade your personal information 
                to third parties for marketing purposes.
              </p>

              <p className="text-muted-foreground mt-4">
                <strong>5.3 Legal Requirements:</strong> We may disclose your information if required by law or in response 
                to valid legal requests.
              </p>

              <p className="text-muted-foreground mt-4">
                <strong>5.4 Business Transfers:</strong> If we are involved in a merger, acquisition, or sale of assets, 
                your information may be transferred as part of that transaction.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">6. Data Retention</h2>
            <p className="text-muted-foreground">
              We retain your information for as long as your account is active or as needed to provide you services. 
              If you close your account, we will delete or anonymize your personal information within 90 days, except 
              where we are required to retain it for legal or business purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">7. Data Security</h2>
            <p className="text-muted-foreground">
              We implement appropriate technical and organizational security measures to protect your information, including:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication requirements</li>
              <li>Monitoring for suspicious activity</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">8. Your Rights and Choices</h2>
            <div className="space-y-3">
              <p className="text-muted-foreground">
                <strong>8.1 Access and Update:</strong> You can access and update your account information through your 
                profile settings.
              </p>
              <p className="text-muted-foreground">
                <strong>8.2 Data Deletion:</strong> You can request deletion of your account and personal data by 
                contacting us at support@earnmyedge.com.
              </p>
              <p className="text-muted-foreground">
                <strong>8.3 Marketing Communications:</strong> You can opt out of promotional emails by clicking the 
                unsubscribe link or adjusting your notification settings.
              </p>
              <p className="text-muted-foreground">
                <strong>8.4 Push Notifications:</strong> You can disable push notifications through your device or browser 
                settings.
              </p>
              <p className="text-muted-foreground">
                <strong>8.5 Cookies:</strong> You can control cookies through your browser settings, though this may 
                limit functionality.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">9. Children's Privacy</h2>
            <p className="text-muted-foreground">
              Our Service is not intended for users under the age of 18. We do not knowingly collect personal information 
              from children. If we learn we have collected information from a child under 18, we will delete it promptly.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">10. International Data Transfers</h2>
            <p className="text-muted-foreground">
              Your information may be transferred to and processed in countries other than your country of residence. 
              These countries may have different data protection laws. By using the Service, you consent to such transfers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">11. California Privacy Rights</h2>
            <p className="text-muted-foreground">
              California residents have additional rights under the California Consumer Privacy Act (CCPA), including the 
              right to know what personal information we collect, the right to delete personal information, and the right 
              to opt-out of the sale of personal information (we do not sell personal information).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">12. Changes to This Privacy Policy</h2>
            <p className="text-muted-foreground">
              We may update this Privacy Policy from time to time. We will notify you of material changes by posting the 
              new Privacy Policy on this page and updating the "Last updated" date. Your continued use of the Service 
              after changes constitutes acceptance of the updated Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">13. Contact Us</h2>
            <p className="text-muted-foreground">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <p className="text-muted-foreground font-medium mt-2">
              Email: support@earnmyedge.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
